import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import api from '../../api/axios';
import { QualitySummary } from './QualitySummary';

// "How this plan was made" — read-only record frozen at publish time, plus a
// live count of shifts changed since then. Admins, published plans only.

const when = (s) => {
  try { return format(parseISO(`${s.replace(' ', 'T')}Z`), "EEE d MMM yyyy, HH:mm"); } catch { return s; }
};

export default function PublishSnapshot({ periodId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState({});

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.get(`/shifts/roster-periods/${periodId}/publish-snapshot`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ snapshot: null }); });
    api.get('/shifts/generator-settings')
      .then((r) => { if (!cancelled) setLabels(Object.fromEntries((r.data.rules || []).map((x) => [x.key, x.label]))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [periodId]);

  if (!data) return null;
  const s = data.snapshot;

  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: '12px' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', width: '100%', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer', textAlign: 'left', minHeight: '36px', flexWrap: 'wrap',
        }}
      >
        <strong style={{ fontSize: '0.92rem' }}>How this plan was made</strong>
        {s && (
          <span
            style={{
              fontSize: '0.78rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
              background: data.changed_since_publishing ? 'var(--warning-light)' : 'var(--success-light)',
              color: data.changed_since_publishing ? 'var(--text)' : 'var(--success)',
            }}
          >
            Changed since publishing: {data.changed_since_publishing}
          </span>
        )}
        <ChevronDown size={14} style={{ marginLeft: 'auto', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
      </button>
      {open && (
        s ? (
          <div style={{ marginTop: '10px', fontSize: '0.84rem', lineHeight: 1.55 }}>
            <p style={{ margin: '0 0 4px' }}>
              Published {when(s.published_at)}{s.published_by ? ` by ${s.published_by}` : ''}.
            </p>
            <p style={{ margin: '0 0 4px', color: 'var(--text-light)' }}>
              {s.variant != null ? `Generated with variant ${s.variant}` : 'Not generated by the generator'}
              {s.levels && (s.changed_levels.length
                ? ` · changed levels: ${s.changed_levels.map((k) => `${labels[k] || k} ${s.levels[k].replace('_', ' ')}`).join(', ')}`
                : ' · default levels')}
              {s.version_id ? ` · from draft version ${s.version_id}` : ''}.
            </p>
            <p style={{ margin: '0 0 10px', color: 'var(--text-light)' }}>
              {s.hand_changes == null
                ? 'Changes by hand after the last generation: not recorded (generated before versions existed).'
                : `${s.hand_changes} shift${s.hand_changes === 1 ? '' : 's'} changed by hand after the last generation`}
              {s.hand_changes != null && ` · ${s.pinned_count} pinned`}
              {` · ${s.shifts} shifts in total.`}
            </p>
            <QualitySummary lines={s.quality} />
            <p style={{ margin: 0, fontSize: '0.76rem', color: 'var(--text-muted)' }}>
              Frozen at publish time — later swaps and changes only update the counter above.
            </p>
          </div>
        ) : (
          <p style={{ margin: '8px 0 0', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
            No record — this plan was published before publish records existed.
          </p>
        )
      )}
    </div>
  );
}
